import { getLastUpdatedBy, type User, usersQuery } from '@atlassian/search-client';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import { ProductKeys } from '../../../../../common/constants/products';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { loadRecommendedUsers } from '../utils/recommendedUser';

const MAX_SEARCHED_OPTIONS_RESULTS = 5;

const loadInitialOptions = async (config: CloudConfig) => {
	return loadRecommendedUsers(config);
};

const searchlastUpdatedBy = async (query: string, config: CloudConfig) => {
	if (!config.intl) {
		return []; // base on existing interface, can only query with intl
	}

	const productKey =
		config.primaryProduct === 'jira' || config.primaryProduct === 'confluence'
			? config.primaryProduct
			: 'confluence';

	const response = await getLastUpdatedBy({
		query,
		cloudId: config.cloudId || '',
		userId: config.userId || '',
		productKey,
		maxNumberOfResults: MAX_SEARCHED_OPTIONS_RESULTS,
		intl: config.intl,
	});

	if (!response) {
		return [];
	}

	return response.map((user) => ({
		trackingKey: user.id,
		value: user.id,
		label: user.name,
		avatarUrl: user.avatarUrl,
		queryParamValue: user.id,
		product: config.product,
	}));
};

export const filter: Readonly<{
    id: "lastUpdatedBy";
    type: "single-select" | "multi-select";
    queryParams: {
        key: string;
    };
    products: ProductKeys[];
    universal: boolean;
    customValueFields?: string[];
    withState: (state: unknown, config: CloudConfig) => import("../../../../../common/constants/filters/select-filter/types").SelectFilterMonad<"lastUpdatedBy">;
    getInitialState: (config: CloudConfig) => import("../../../../../common/constants/filters/select-filter/types").SelectFilterMonad<"lastUpdatedBy">;
    isCompatible: (filterId?: string) => boolean;
    isSupportCustomValue: boolean;
    isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'lastUpdatedBy',
	products: [ProductKeys.Salesforce, ProductKeys.Hubspot],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'lastUpdatedBy',
		fetcher: async (queryParams, config) => {
			const initialUsersResponse = await usersQuery(queryParams);

			const allLastUpdatedBy = initialUsersResponse.data?.users ?? ([] satisfies User[]);

			const userIds = new Set();

			const lastUpdatedByOptions = allLastUpdatedBy
				.filter((user) => !userIds.has(user.accountId) && userIds.add(user.accountId)) // Remove duplicates
				.map((user) => ({
					trackingKey: user.accountId,
					value: user.accountId,
					label: user.name,
					avatarUrl: user.picture,
					queryParamValue: user.accountId,
					product: config.product,
				}));
			return lastUpdatedByOptions;
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return loadInitialOptions(config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return searchlastUpdatedBy(query, config);
		},
	},
});
